import { useEffect, useLayoutEffect, useRef, useState, type Dispatch, type SetStateAction } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { FormField } from '@/components/ui/form-field'
import { Input } from '@/components/ui/input'
import { ArtifactFormSkeleton } from '@/components/layout/artifact-form-skeleton'
import {
  useOrganisations,
  useManagedOrganisationDetail,
  type Organisation,
  type CreateOrganisationData,
  type UpdateOrganisationData,
} from '@/hooks/use-organisations'
import {
  ArtifactCanvasBody,
  ArtifactCanvasFooterBar,
  ArtifactCanvasSaveActions,
} from '@/components/artifact/artifact-canvas-layout'
import { CLIENT_LOCALE } from '@/lib/client-locale'
import {
  defaultEntityCountryCode,
  entityCountryForEdit,
} from '@/utils/entity-location-fields'
import { prepareWebsiteForSave } from '@/lib/normalize-website-url'
import { EntityLocationFields } from '@/components/ui/entity-location-fields'
import { EntityOrganisationCompanyFields } from '@/components/ui/entity-organisation-company-fields'
import { EntityPublicContactFields, entityPublicContactFrom } from '@/components/ui/entity-public-contact-fields'
import { extraSocialContactFrom, extraSocialFieldsFrom } from '@/lib/social-networks'
import {
  foundedYearDraftValue,
  parseFoundedYearForSave,
} from '@/lib/organisation-company-meta'
import { shallowDraftEqual } from '@/utils/draft-equality'
import { OrganisationNameLookupField } from '@/components/onboarding/organisation-name-lookup-field'
import { isOrganisationPrefillEnabled } from '@/constants'
import {
  draftOrganisationDetails,
  type OrganisationPrefillDraft,
} from '@/lib/organisation-prefill-api'

type CreateDraft = OrganisationDetailsDraft & { name: string }

/** Fill only empty create fields from a company draft; found socials become visible rows. */
function applyPrefillToCreateDraft(current: CreateDraft, draft: OrganisationPrefillDraft): CreateDraft {
  const keep = (value: string | undefined, next: string) => (value?.trim() ? value : next)
  const next: CreateDraft = {
    ...current,
    industry: keep(current.industry, draft.industry),
    company_size: keep(current.company_size, draft.company_size),
    founded_year: keep(current.founded_year, foundedYearDraftValue(draft.founded_year)),
    city: keep(current.city, draft.city),
    country:
      !current.city?.trim() && draft.city && draft.country ? draft.country : current.country,
  }
  const order = [...(current.extra_social_order ?? [])]
  for (const [field, url] of Object.entries(draft.socials)) {
    const key = field as keyof CreateDraft
    if (!url || (current[key] as string | undefined)?.trim()) continue
    ;(next as Record<string, unknown>)[key] = url
    const network = field.replace(/_url$/, '')
    if (!order.includes(network)) order.push(network)
  }
  next.extra_social_order = order
  return next
}

export type OrganisationDetailsDraft = Omit<UpdateOrganisationData, 'founded_year' | 'description'> & {
  founded_year?: string
}

function buildDraft(org: Organisation): OrganisationDetailsDraft {
  return {
    name: org.name ?? '',
    website: org.website ?? '',
    industry: org.industry ?? '',
    company_size: org.company_size ?? '',
    founded_year: foundedYearDraftValue(org.founded_year),
    city: org.city ?? '',
    country: entityCountryForEdit(org.country, CLIENT_LOCALE),
    linkedin_url: org.linkedin_url ?? '',
    x_url: org.x_url ?? '',
    ...extraSocialContactFrom(org),
  }
}

function buildCreateDraft(): OrganisationDetailsDraft & { name: string } {
  return {
    name: '',
    website: '',
    industry: '',
    company_size: '',
    founded_year: '',
    city: '',
    country: defaultEntityCountryCode(CLIENT_LOCALE),
    linkedin_url: '',
    x_url: '',
    ...extraSocialContactFrom({}),
  }
}

type OrgDraftState = OrganisationDetailsDraft & { name?: string }

function patchOrgDraft(
  isCreatePhase: boolean,
  patch: Partial<OrgDraftState>,
  setCreateDraft: Dispatch<SetStateAction<OrganisationDetailsDraft & { name: string }>>,
  setDraft: Dispatch<SetStateAction<OrganisationDetailsDraft>>,
) {
  if (isCreatePhase) {
    setCreateDraft((current) => ({ ...current, ...patch }))
  } else {
    setDraft((current) => ({ ...current, ...patch }))
  }
}

interface OrganisationDetailsCanvasProps {
  /** Omit for create-from-list; slug is assigned after POST. */
  slug?: string
  /** `create` — name on first save; `profile` — company details only (name/logo live in org settings). */
  variant?: 'create' | 'profile'
  onClose: () => void
  /** After successful PUT save; invoked before closing. */
  onSaved?: (updated: Organisation) => void
  /** After successful POST create; canvas may stay open for optional profile fields. */
  onCreated?: (organisation: Organisation) => void
}

/** Reads org from `['organisation', slug]` cache so logo upload/delete updates preview without closing canvas. */
export function OrganisationDetailsCanvas({
  slug: slugProp,
  variant = 'create',
  onClose,
  onSaved,
  onCreated,
}: OrganisationDetailsCanvasProps) {
  const queryClient = useQueryClient()
  const [committedSlugAfterCreate, setCommittedSlugAfterCreate] = useState<string | null>(null)
  const effectiveSlug = slugProp ?? committedSlugAfterCreate ?? undefined
  const isCreatePhase = effectiveSlug === undefined

  const { data: organisation } = useManagedOrganisationDetail(effectiveSlug)
  const {
    updateOrganisation,
    createOrganisation,
    isUpdating,
    isCreating,
    updateError,
    createError,
  } = useOrganisations()

  /** One-time hydrate from server when canvas opens or slug appears — do not wipe user typing when logo/cache updates mid-edit. */
  const didHydrateDraftRef = useRef(false)
  const [createDraft, setCreateDraft] = useState<CreateDraft>(() => buildCreateDraft())
  const [draft, setDraft] = useState<OrganisationDetailsDraft>(() => ({
    name: '',
    website: '',
    industry: '',
    city: '',
    country: defaultEntityCountryCode(CLIENT_LOCALE),
    linkedin_url: '',
    x_url: '',
    ...extraSocialContactFrom({}),
  }))
  const [baselineDraft, setBaselineDraft] = useState<OrganisationDetailsDraft | null>(null)
  const [validationError, setValidationError] = useState<string | null>(null)
  const [mutationError, setMutationError] = useState<string | null>(null)
  const prefillEnabled = isOrganisationPrefillEnabled()
  const [pickedDomain, setPickedDomain] = useState<string | null>(null)
  const [prefillStatus, setPrefillStatus] = useState<string | null>(null)
  const prefillAbortRef = useRef<AbortController | null>(null)

  useEffect(() => () => prefillAbortRef.current?.abort(), [])

  const handlePickedDomain = (domain: string | null) => {
    setPickedDomain(domain)
    prefillAbortRef.current?.abort()
    if (!domain) {
      setPrefillStatus(null)
      return
    }
    setCreateDraft((current) =>
      current.website?.trim() ? current : { ...current, website: `https://${domain}` },
    )
    const controller = new AbortController()
    prefillAbortRef.current = controller
    setPrefillStatus(`Reading ${domain}…`)
    draftOrganisationDetails(domain, controller.signal)
      .then((draft) => {
        if (controller.signal.aborted) return
        setCreateDraft((current) => applyPrefillToCreateDraft(current, draft))
        const found = draft.industry || draft.city || Object.keys(draft.socials).length
        setPrefillStatus(
          found
            ? `Suggested from ${domain}. Check the details before you create the organisation.`
            : `We couldn't read ${domain}. Fill in the details below.`,
        )
      })
      .catch(() => {
        if (!controller.signal.aborted) {
          setPrefillStatus(`We couldn't read ${domain}. Fill in the details below.`)
        }
      })
  }

  useLayoutEffect(() => {
    didHydrateDraftRef.current = false
  }, [effectiveSlug])

  useLayoutEffect(() => {
    if (!organisation || didHydrateDraftRef.current) return
    didHydrateDraftRef.current = true
    const next = buildDraft(organisation)
    setDraft(next)
    setBaselineDraft(next)
  }, [organisation])

  useEffect(() => {
    setValidationError(null)
    setMutationError(null)
  }, [organisation?.id])

  const activeDraft = isCreatePhase ? createDraft : draft

  const isProfileVariant = variant === 'profile' && !isCreatePhase

  const isDirty =
    baselineDraft !== null && organisation && !isCreatePhase
      ? !shallowDraftEqual(
          (isProfileVariant
            ? { ...draft, name: baselineDraft.name }
            : draft) as Record<string, string | undefined>,
          baselineDraft as Record<string, string | undefined>,
        )
      : false

  const handleCreate = () => {
    if (!createDraft.name.trim()) {
      setValidationError('Organisation name is required')
      return
    }
    const { website, error: websiteErr } = prepareWebsiteForSave(createDraft.website)
    if (websiteErr) {
      setValidationError(websiteErr)
      return
    }
    const { founded_year, error: foundedErr } = parseFoundedYearForSave(createDraft.founded_year)
    if (foundedErr) {
      setValidationError(foundedErr)
      return
    }
    setValidationError(null)
    setMutationError(null)

    const createData: CreateOrganisationData = {
      name: createDraft.name.trim(),
      website,
      industry: createDraft.industry,
      company_size: createDraft.company_size,
      founded_year,
      city: createDraft.city,
      country: createDraft.country,
      linkedin_url: createDraft.linkedin_url,
      x_url: createDraft.x_url,
      ...extraSocialFieldsFrom(createDraft),
      extra_social_order: createDraft.extra_social_order,
    }

    createOrganisation(createData, {
      onSuccess: (created) => {
        queryClient.setQueryData(['organisation', created.slug], created)
        setCommittedSlugAfterCreate(created.slug)
        didHydrateDraftRef.current = false
        onCreated?.(created)
      },
      onError: (err) => {
        setMutationError(err instanceof Error ? err.message : 'Could not create organisation')
      },
    })
  }

  const handleSave = () => {
    if (!effectiveSlug || isCreatePhase) return
    if (!isProfileVariant && !draft.name?.trim()) {
      setValidationError('Organisation name is required')
      return
    }
    const { website, error: websiteErr } = prepareWebsiteForSave(draft.website)
    if (websiteErr) {
      setValidationError(websiteErr)
      return
    }
    const { founded_year, error: foundedErr } = parseFoundedYearForSave(draft.founded_year)
    if (foundedErr) {
      setValidationError(foundedErr)
      return
    }
    setValidationError(null)
    setMutationError(null)

    updateOrganisation(
      {
        slug: effectiveSlug,
        data: isProfileVariant
          ? {
              website,
              industry: draft.industry,
              company_size: draft.company_size,
              founded_year,
              city: draft.city,
              country: draft.country,
              linkedin_url: draft.linkedin_url,
              x_url: draft.x_url,
              ...extraSocialFieldsFrom(draft),
              extra_social_order: draft.extra_social_order,
            }
          : {
              ...draft,
              name: draft.name?.trim(),
              website,
              company_size: draft.company_size,
              founded_year,
            },
      },
      {
        onSuccess: (updated) => {
          onSaved?.(updated)
          onClose()
        },
        onError: (err) => {
          setMutationError(err instanceof Error ? err.message : 'Could not save organisation')
        },
      }
    )
  }

  const combinedRemoteError =
    mutationError ??
    (updateError instanceof Error ? updateError.message : updateError ? String(updateError) : null) ??
    (createError instanceof Error ? createError.message : createError ? String(createError) : null)

  if (!isCreatePhase && !organisation) {
    return (
      <ArtifactCanvasBody>
        <ArtifactFormSkeleton fields={5} ariaLabel="Loading organisation" />
      </ArtifactCanvasBody>
    )
  }

  return (
    <ArtifactCanvasBody className="flex h-full min-h-0 flex-col space-y-0 p-0">
      <form
        className="min-h-0 flex-1 space-y-6 overflow-y-auto p-6"
        onSubmit={(e) => e.preventDefault()}
      >
        {isCreatePhase && prefillEnabled ? (
          <div className="space-y-2">
            <OrganisationNameLookupField
              id="artifact-org-name"
              label="Organisation name *"
              placeholder="Organisation name"
              value={createDraft.name}
              onChange={(name) => {
                setCreateDraft((current) => ({ ...current, name }))
                setValidationError(null)
              }}
              domain={pickedDomain}
              onDomainChange={handlePickedDomain}
              disabled={isCreating}
            />
            {prefillStatus ? (
              <p className="text-sm text-muted-foreground" role="status">
                {prefillStatus}
              </p>
            ) : null}
          </div>
        ) : isCreatePhase ? (
          <FormField label="Organisation name *" htmlFor="artifact-org-name">
            <Input
              id="artifact-org-name"
              value={activeDraft.name ?? ''}
              onChange={(e) => {
                patchOrgDraft(isCreatePhase, { name: e.target.value }, setCreateDraft, setDraft)
                setValidationError(null)
              }}
              placeholder="Organisation name"
            />
          </FormField>
        ) : null}

        {!isCreatePhase && isProfileVariant ? (
          <p className="text-sm text-muted-foreground">
            Organisation name and logo are managed in organisation settings.
          </p>
        ) : null}

        {!isCreatePhase && variant === 'create' ? (
          <p className="text-sm text-muted-foreground">
            Add a logo anytime from organisation settings.
          </p>
        ) : null}

        <FormField label="Industry" htmlFor="artifact-org-industry">
          <Input
            id="artifact-org-industry"
            value={activeDraft.industry ?? ''}
            onChange={(e) => {
              patchOrgDraft(isCreatePhase, { industry: e.target.value }, setCreateDraft, setDraft)
            }}
            placeholder="e.g., Technology"
          />
        </FormField>

        <EntityOrganisationCompanyFields
          idPrefix="artifact-org"
          value={{
            company_size: activeDraft.company_size ?? '',
            founded_year: activeDraft.founded_year ?? '',
          }}
          onChange={(patch) => patchOrgDraft(isCreatePhase, patch, setCreateDraft, setDraft)}
        />

        <EntityLocationFields
          idPrefix="artifact-org"
          value={{
            city: activeDraft.city ?? '',
            country: activeDraft.country ?? '',
          }}
          onChange={(patch) => patchOrgDraft(isCreatePhase, patch, setCreateDraft, setDraft)}
        />

        <EntityPublicContactFields
          idPrefix="artifact-org"
          value={entityPublicContactFrom(activeDraft)}
          onChange={(patch) => patchOrgDraft(isCreatePhase, patch, setCreateDraft, setDraft)}
        />

        {(validationError || combinedRemoteError) && (
          <div className="rounded-md border border-destructive/20 bg-destructive/10 px-3 py-2 text-sm text-destructive">
            {validationError ?? combinedRemoteError}
          </div>
        )}
      </form>

      <ArtifactCanvasFooterBar
        actions={
          <ArtifactCanvasSaveActions
            onClose={onClose}
            onSave={isCreatePhase ? handleCreate : handleSave}
            isDirty={isCreatePhase ? Boolean(createDraft.name.trim()) : isDirty}
            isSaving={isCreatePhase ? isCreating : isUpdating}
            saveLabel={isCreatePhase ? 'Create organisation' : 'Save'}
            savingLabel={isCreatePhase ? 'Creating…' : 'Saving…'}
            saveDisabled={
              isCreatePhase
                ? !createDraft.name.trim()
                : !isDirty || (!isProfileVariant && !draft.name?.trim())
            }
          />
        }
      />
    </ArtifactCanvasBody>
  )
}
